/**
 * Sprint 54.2 (Admin Console Recovery — Batch 2) — Owner-friendly
 * feature catalog.
 *
 * The Sprint 14 EntitlementsPage rendered raw entitlement keys (e.g.
 * `devices.approval_required`, `fleet.intelligence.read`) verbatim.
 * The Owner smoke flagged that as unfriendly: an operator should be
 * able to scan "what features does this tenant have?" without
 * mentally translating a closed-enum identifier.
 *
 * This module owns the *display* mapping only. The wire format (the
 * closed entitlement keys) is unchanged — the Admin Console's PUT /
 * DELETE calls still use the canonical key. The grouping and labels
 * here are purely UI affordances:
 *
 *   - `FEATURE_GROUPS` defines an ordered set of product areas.
 *   - Each group lists known keys + an i18n label key + an optional
 *     short description.
 *   - Unknown keys (forward-compat: a new entitlement landed
 *     server-side before the catalog learned about it) fall into the
 *     `"other"` group with the raw key as label.
 *
 * Test pin (sprint-54-2.test.ts):
 *   - The closed enum of group ids is stable.
 *   - Every known key maps to exactly one group.
 */

export type FeatureGroupId =
  | "devices"
  | "fleet_intelligence"
  | "demo"
  | "reporting"
  | "platform"
  | "other";

export interface FeatureGroupDef {
  readonly id: FeatureGroupId;
  readonly labelKey: string;
  /** Closed list of canonical entitlement keys that belong here. */
  readonly knownKeys: ReadonlyArray<string>;
}

/**
 * The catalog is intentionally closed. New entitlement keys must be
 * added here when they ship, OR they fall through to the `"other"`
 * group with no friendlier label. That is on purpose — the IA
 * structure should drift forward consciously, not silently.
 */
export const FEATURE_GROUPS: ReadonlyArray<FeatureGroupDef> = [
  {
    id: "devices",
    labelKey: "admin.entitlements.group.devices",
    knownKeys: [
      "devices.approval_required",
      "devices.byod_self_register",
      "devices.managed_hardware_only",
      "devices.allowed_vendors",
    ],
  },
  {
    id: "fleet_intelligence",
    labelKey: "admin.entitlements.group.fleet_intelligence",
    knownKeys: [
      "fleet.intelligence.read",
      "fleet.intelligence.write",
      "fleet.intelligence.alerts",
      "fleet.intelligence.summary",
    ],
  },
  {
    id: "demo",
    labelKey: "admin.entitlements.group.demo",
    knownKeys: ["demo.scenarios.read", "demo.scenarios.apply"],
  },
  {
    id: "reporting",
    labelKey: "admin.entitlements.group.reporting",
    knownKeys: [
      "reports.scheduled",
      "reports.export.csv",
      "reports.export.pdf",
      "reports.audit.read",
    ],
  },
  {
    id: "platform",
    labelKey: "admin.entitlements.group.platform",
    knownKeys: ["platform.support_inbox", "platform.telegram_bot", "platform.diagnostics"],
  },
  {
    id: "other",
    labelKey: "admin.entitlements.group.other",
    // The `"other"` bucket has no known keys; it catches everything
    // the catalog hasn't learned about yet.
    knownKeys: [],
  },
];

/**
 * Resolve a canonical entitlement key to its group. Returns the
 * `"other"` group for keys not pinned in `FEATURE_GROUPS`. The
 * lookup is O(catalog size), which is fine — the catalog is on the
 * order of a few dozen keys.
 */
export function groupForKey(key: string): FeatureGroupDef {
  for (const group of FEATURE_GROUPS) {
    if (group.knownKeys.includes(key)) return group;
  }
  // Fallback. The `!` is safe because the catalog always ends with
  // the `"other"` sentinel.
  return FEATURE_GROUPS[FEATURE_GROUPS.length - 1]!;
}

/**
 * Stable display order for a set of keys: groups in catalog order,
 * keys within a group in catalog order, then unknown keys in
 * alphabetical order under `"other"`.
 */
export function groupKeys(keys: ReadonlyArray<string>): ReadonlyArray<{
  readonly group: FeatureGroupDef;
  readonly keys: ReadonlyArray<string>;
}> {
  const buckets = new Map<FeatureGroupId, string[]>();
  for (const group of FEATURE_GROUPS) buckets.set(group.id, []);
  for (const key of keys) {
    const group = groupForKey(key);
    buckets.get(group.id)!.push(key);
  }
  // Preserve catalog order for known keys; alphabetize the "other"
  // bucket since it has no canonical order.
  return FEATURE_GROUPS.map((group) => {
    const list = buckets.get(group.id) ?? [];
    if (group.id === "other") list.sort((a, b) => a.localeCompare(b));
    else list.sort((a, b) => group.knownKeys.indexOf(a) - group.knownKeys.indexOf(b));
    return { group, keys: list as ReadonlyArray<string> };
  }).filter((entry) => entry.keys.length > 0);
}
